import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
// Named import — siehe Begründung in main.tsx (Tree-Shaking für Session Replay).
import { setUser as sentrySetUser } from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { readPendingInvite } from "@/lib/householdInvite";
import { isNative } from "@/lib/platform";
import { biometrieLoginNutzer } from "@/lib/biometrieMerker";
import { anzeigeCacheLeeren } from "@/lib/anzeigeCache";
import { updateMetaPixelUserData, trackEventWithCAPI } from "@/lib/metaPixel";
import { ladeMetaPixelUserData } from "@/lib/metaPixelUserData";
import { getUtmParams, getFirstTouch } from "@/hooks/useUtmCapture";
import { edgeFunctionErrorMessage } from "@/lib/edgeFunction";

export type SignOutOptionen = {
  /** App mit eingerichteter Face-ID-Anmeldung: Eintrag behalten (nur lokal abmelden). Standard: entfernen. */
  biometrieBehalten?: boolean;
};

const RECOVERY_KEY = "password_recovery_pending";
const RECOVERY_QUERY_PARAM = "recovery";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPasswordRecovery: boolean;
  /** true, wenn eine bestehende Sitzung ohne expliziten Logout weggefallen ist
   *  (Refresh-Token abgelaufen/ungültig). Seiten können dann einen Hinweis zeigen,
   *  statt sofort umzuleiten und offene Eingaben zu verlieren. */
  sessionExpired: boolean;
  signUp: (email: string, password: string, fullName: string, phone: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: (optionen?: SignOutOptionen) => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  clearPasswordRecovery: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check for recovery token - use lazy initialization to avoid module-load issues
const getInitialRecoveryState = (): boolean => {
  try {
    const hash = window.location.hash;
    const searchParams = new URLSearchParams(window.location.search);
    const hasRecoveryHash = Boolean(hash && hash.includes("type=recovery"));
    const hasRecoveryQuery = searchParams.has(RECOVERY_QUERY_PARAM);
    const hasPkceCode = searchParams.has("code");
    const hasStoredRecovery = sessionStorage.getItem(RECOVERY_KEY) === "true";

    if (hasRecoveryHash || hasRecoveryQuery) {
      sessionStorage.setItem(RECOVERY_KEY, "true");
      return true;
    }

    if (hasStoredRecovery && hasPkceCode) {
      return true;
    }

    if (hasStoredRecovery) {
      sessionStorage.removeItem(RECOVERY_KEY);
    }

    return false;
  } catch {
    return false;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Use lazy initialization to compute initial state safely
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(() => getInitialRecoveryState());
  const [sessionExpired, setSessionExpired] = useState(false);
  // Zuletzt gesetzte user-ID: setUser nur bei Identitätswechsel. Der stündliche
  // TOKEN_REFRESHED liefert ein neues user-Objekt mit gleicher ID; ein neues Objekt
  // im Context ließ Dashboard und Karten jedes Mal komplett neu laden.
  const userIdRef = useRef<string | null>(null);
  // Gesetzt, solange signOut() läuft: unterscheidet den Logout-Knopf von einer
  // abgelaufenen Sitzung (beide enden in SIGNED_OUT).
  const explicitSignOutRef = useRef(false);

  // Sentry-User-Kontext. Ohne den steht bei jedem Frontend-Issue "Users: 0" —
  // wir konnten bisher nicht sehen, ob ein Fehler eine einzelne Kundin oder
  // fünfzig trifft, und Meldungen aus dem Support ("bei Katja kommt das
  // dauernd") ließen sich nicht gegen Sentry gegenprüfen.
  //
  // Bewusst NUR die user_id — kein Name, keine E-Mail, keine IP. Der Support
  // kann eine ID jederzeit über die profiles-Tabelle auflösen; damit landen
  // keine personenbezogenen Daten bei einem US-Dienstleister.
  useEffect(() => {
    if (user?.id) {
      sentrySetUser({ id: user.id });
    } else {
      sentrySetUser(null);
    }
  }, [user?.id]);

  useEffect(() => {
    let isMounted = true;
    
    // Meta Pixel Advanced Matching. Perf Welle 5: getSession UND onAuthStateChange riefen
    // das hier beim Start mehrfach auf (je ein profiles-Request). Jetzt lädt
    // ladeMetaPixelUserData höchstens einmal je Nutzer-ID, nur mit Marketing-Consent und
    // erst verzögert; hier zusätzlich nur EIN fbq-Update je Nutzer pro Seitenladung.
    const pixelUserIds = new Set<string>();
    const sendUserDataToPixel = (session: Session | null) => {
      const u = session?.user;
      if (!u || pixelUserIds.has(u.id)) return;
      const daten = ladeMetaPixelUserData(u);
      if (!daten) return; // kein Consent → kein Pixel; späterer Aufruf darf es erneut versuchen
      pixelUserIds.add(u.id);
      void daten.then((d) => updateMetaPixelUserData(d));
    };

    const applySession = (session: Session | null, forceUserUpdate = false) => {
      setSession(session);
      const nextUser = session?.user ?? null;
      const nextId = nextUser?.id ?? null;
      if (forceUserUpdate || nextId !== userIdRef.current) {
        userIdRef.current = nextId;
        setUser(nextUser);
      }
      if (nextUser) setSessionExpired(false);
    };

    // Set up auth state listener FIRST (for ongoing changes)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        // Sitzung weg, obwohl vorher jemand eingeloggt war und niemand auf „Abmelden"
        // gedrückt hat → abgelaufen (z. B. Refresh-Token ungültig).
        if (!session?.user && userIdRef.current && !explicitSignOutRef.current) {
          setSessionExpired(true);
        }
        if (event === "SIGNED_OUT") {
          explicitSignOutRef.current = false;
          // Sofortanzeige-Speicher (Hundeseite) gehört zur Sitzung: nichts für das nächste Konto liegen lassen.
          anzeigeCacheLeeren();
        }
        // USER_UPDATED: gleiche ID, aber geänderte Metadaten → Objekt durchreichen.
        applySession(session, event === "USER_UPDATED");
        
        // Send user data to Meta Pixel on login/signup
        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
          sendUserDataToPixel(session);
        }

        // Track CompleteRegistration for brand-new signups only.
        //
        // A3 (18.09.2026): Dies ist seit heute die EINZIGE Stelle — Auth.tsx
        // feuerte dasselbe Event direkt nach signUp() noch einmal, Meta zählte
        // jede Registrierung doppelt. Diese Stelle bleibt, weil sie auch
        // Registrierungen erwischt, die nicht über das Auth-Formular laufen.
        //
        // Jetzt über Pixel + CAPI mit gemeinsamer event_id (dedupliziert) und
        // external_id = user.id: ohne die kann Meta ein Server-Event keinem
        // Menschen zuordnen, wenn iOS/Adblocker den Pixel wegnimmt.
        // Haushalts-Mitglieder (KA-29) sind keine neuen Kunden: kein Registrierungs-Event.
        if (event === "SIGNED_IN" && session?.user && !session.user.user_metadata?.household_invite) {
          const createdAt = new Date(session.user.created_at).getTime();
          const now = Date.now();
          const fiveMinutes = 5 * 60 * 1000;
          if (now - createdAt < fiveMinutes) {
            // Reload-Guard: SIGNED_IN kann innerhalb dieser fünf Minuten erneut
            // kommen (zweiter Tab, Neuladen direkt nach der Registrierung).
            // Schlüssel je Nutzer, damit ein Gerätewechsel sauber bleibt.
            const guardKey = `cr_fired_${session.user.id}`;
            let bereitsGefeuert = false;
            try {
              bereitsGefeuert = localStorage.getItem(guardKey) === "1";
              if (!bereitsGefeuert) localStorage.setItem(guardKey, "1");
            } catch { /* Storage gesperrt → lieber doppelt als gar nicht */ }

            if (!bereitsGefeuert) {
              void trackEventWithCAPI(
                "CompleteRegistration",
                {
                  external_id: session.user.id,
                  ...(session.user.email ? { em: session.user.email } : {}),
                  country: "de",
                },
                { content_name: "signup" },
                undefined,
                undefined,
                // Registrierung ist ein bewusster Nutzer-Schritt — hier darf eine
                // _fbp-ID gesetzt werden, damit das Server-Event der Anzeige
                // zugeordnet werden kann (s. trackEventWithCAPI).
                { ensureFbp: true },
              ).catch(() => { /* Tracking darf die Anmeldung nie stören */ });
            }
          }
        }

        // Handle password recovery event
        if (event === "PASSWORD_RECOVERY") {
          sessionStorage.setItem(RECOVERY_KEY, "true");
          setIsPasswordRecovery(true);
        }
      }
    );

    // THEN check for existing session (controls loading state)
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;
        applySession(session);
        // Send user data to Meta Pixel for already logged-in users
        sendUserDataToPixel(session);
      } catch (err) {
        console.error("[AuthContext] Error getting session:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);
  const signUp = useCallback(async (email: string, password: string, fullName: string, phone: string) => {
    const redirectUrl = `${window.location.origin}/`;

    // Check for benefits_me referral source
    const referralSource = typeof window !== 'undefined' ? localStorage.getItem("referral_source") : null;

    // First-Touch-Marketing-Herkunft mitgeben. Der Schnuppertag-Pfad braucht das
    // nicht — book-schnuppertag schreibt die Signale schon auf die Buchung, und
    // der profiles-Trigger liest sie dort ab. Die Direkt-Registrierung hat aber
    // keine Buchung, aus der man sie lesen koennte: ohne diese Zeilen bleibt sie
    // fuer immer "unattribuiert". Quelle ist derselbe First-Touch-Cookie, den
    // useUtmCapture app-weit setzt (90 T, ueberschreibt nie).
    // Gegenstueck: capture_profile_attribution() (Migration 20260716180000).
    const utm = getUtmParams();
    const firstTouch = getFirstTouch();
    // Haushalt (KA-29): mit Einladung legt handle_new_user kein eigenes Kundenprofil an.
    const householdInvite = readPendingInvite();

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          phone: phone,
          ...(referralSource ? { referral_source: referralSource } : {}),
          ...(utm ?? {}),
          ...(firstTouch?.referrer ? { referrer: firstTouch.referrer } : {}),
          ...(firstTouch?.landing_page ? { landing_page: firstTouch.landing_page } : {}),
          ...(householdInvite ? { household_invite: householdInvite } : {}),
        },
      },
    });

    return { error: error as Error | null };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { error: error as Error | null };
  }, []);

  const signOut = useCallback(async (optionen?: SignOutOptionen) => {
    explicitSignOutRef.current = true;
    setSessionExpired(false);
    // Gespeicherte Anzeige-Daten sofort weg (auch beim lokalen Abmelden mit Face ID behalten).
    anzeigeCacheLeeren();
    // Push (KA-09): Gerät abmelden, solange die Sitzung noch gilt. Höchstens 1,5 s, darf den
    // Logout nie blockieren oder scheitern lassen; im Web ein No-op ohne Netzwerk.
    // Startbildschirm-Widget leeren (1.0.1): keine Daten des vorigen Kontos auf einem geteilten Handy.
    // Unabhängig vom Push-Abmelden (darf dessen Wettlauf nicht verkürzen), braucht keine Sitzung.
    if (isNative()) void import("@/lib/appWidget").then((m) => m.widgetLeeren()).catch(() => {});
    if (isNative()) try {
      await Promise.race([
        import("@/lib/push").then((m) => m.pushAbmelden()),
        new Promise((resolve) => setTimeout(resolve, 1500)),
      ]);
    } catch { /* egal */ }
    // Face ID / Touch ID (Store-Build 1.0.1): Nur wenn die schnelle Anmeldung auf diesem Gerät
    // eingerichtet ist, wird der Biometrie-Chunk geladen (Merker = synchroner localStorage-Blick).
    // „Behalten“: nur die lokale Sitzung entfernen, der geschützte Token bleibt gültig. Sonst (und wenn
    // das lokale Abmelden nicht geht): Eintrag löschen und normal abmelden (Sitzung widerrufen).
    if (isNative() && biometrieLoginNutzer()) {
      try {
        const bio = await import("@/lib/biometrie");
        if (optionen?.biometrieBehalten && (await bio.lokalAbmelden())) {
          explicitSignOutRef.current = false;
          return;
        }
        await bio.loginEntfernen("abmelden");
      } catch { /* Abmelden geht immer vor */ }
    }
    try {
      await supabase.auth.signOut();
    } finally {
      // Das SIGNED_OUT-Event läuft innerhalb von signOut(); danach Flag sicher zurücksetzen.
      explicitSignOutRef.current = false;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    // Custom flow: invokes our edge function which issues a one-time DB-backed
    // token and emails a /auth/activate?token=... link via Resend. This bypasses
    // Supabase's email-link PKCE flow which is broken by mail-scanner prefetch
    // and device-bound code_verifiers (link clicked on different device than
    // request-issuing device).
    const { data, error } = await supabase.functions.invoke("request-password-reset", {
      body: { email },
    });

    if (error) {
      return { error: new Error(await edgeFunctionErrorMessage(error, "request-password-reset")) };
    }
    if (data && typeof data === "object" && "error" in data && (data as { error?: string }).error) {
      return { error: new Error((data as { error: string }).error) };
    }
    return { error: null };
  }, []);

  const clearPasswordRecovery = useCallback(() => {
    setIsPasswordRecovery(false);
    // Clear the persisted recovery state
    sessionStorage.removeItem(RECOVERY_KEY);
    // Clear the hash from URL
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    session,
    loading,
    isPasswordRecovery,
    sessionExpired,
    signUp,
    signIn,
    signOut,
    resetPassword,
    clearPasswordRecovery,
  }), [user, session, loading, isPasswordRecovery, sessionExpired, signUp, signIn, signOut, resetPassword, clearPasswordRecovery]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
